import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { branchFeatureFlags } from "../config/branchFeatureFlags";
import { _axios } from "../utils/axios";

/**
 * Schematic Plan choice (drawing or uploaded file) and the uploaded file of
 * one application, from /schematic/plan. Error messages from the server are
 * already shown by the axios interceptor.
 */

/**
 * "applicant_file" (Use applicant's schematic) and "none" (No schematic plan)
 * are offered to Offline Tentative only. "image_dimensions" (Upload image +
 * dimensions) is offered to every application when the server says so.
 * BEFORE: export type TSchematicPlanType = "drawing" | "file";
 * BEFORE: export type TSchematicPlanType = "drawing" | "file" | "applicant_file" | "none";
 */
export type TSchematicPlanType = "drawing" | "file" | "applicant_file" | "none" | "image_dimensions";

/**
 * The plot image, four sides (metres) and direction of "Upload image +
 * dimensions". Direction is "1" down, "2" left, "3" up, "4" right.
 */
export type TSchematicImageDimensions = {
  imageUrl: string | null;
  ab: number;
  bc: number;
  cd: number;
  da: number;
  /**
   * AA' and BB' in metres, 0 when not entered yet.
   * BEFORE: aaBb?: string; ("AA' and BB'" as one optional free-text box)
   */
  aa?: number;
  bb?: number;
  /** DE and EF in metres, 0 when not entered. */
  de?: number;
  ef?: number;
  direction: string | null;
};

/** The applicant's own Schematic Plan document (Offline Tentative only). */
export type TApplicantSchematicFile = {
  url: string;
  name: string;
  fileType: "pdf" | "image";
};

export type TSchematicPlanFile = {
  url: string;
  name: string;
  fileType: "pdf" | "image";
  size: number;
  updatedBy?: string;
  updatedByRole?: string;
  updatedAt?: string;
};

export type TSchematicPlan = {
  planType: TSchematicPlanType;
  /** True when Level 3 makes the choice itself: nobody chose before it. */
  levelThreeChooses?: boolean;
  file: TSchematicPlanFile | null;
  /** True for Offline Tentative: the two extra choices are offered. */
  offlineOptions?: boolean;
  /** The applicant's Schematic Plan document, or null when none was uploaded. */
  applicantFile?: TApplicantSchematicFile | null;
  /** True when "Upload image + dimensions" is offered. */
  imageDimensionsOption?: boolean;
  /** The image, sides and direction last saved with the certificate data, or null. */
  imageDimensions?: TSchematicImageDimensions | null;
};

const BASE = "/schematic/plan";
const schematicPlanKey = (applicationId: number) => ["schematic-plan", applicationId];

/** The choice and file; with the feature off it is not requested. */
export function useSchematicPlan(applicationId: number | undefined) {
  return useQuery({
    queryKey: schematicPlanKey(Number(applicationId)),
    queryFn: () =>
      _axios.get<{ success: boolean; data: TSchematicPlan }>(`${BASE}/${applicationId}`),
    select: (result) => result.data?.data ?? null,
    enabled: branchFeatureFlags.ENABLE_SCHEMATIC_FILE_UPLOAD && !!applicationId,
    staleTime: 0,
  });
}

/** Uploads or replaces the file; `onProgress` gets 0-100 while it is sent. */
export function useUploadSchematicFile(applicationId: number) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ file, onProgress }: { file: File; onProgress?: (percent: number) => void }) => {
      const body = new FormData();
      body.append("file", file);
      return _axios
        .post<{ success: boolean; data: TSchematicPlan }>(`${BASE}/${applicationId}/file`, body, {
          headers: { "Content-Type": "multipart/form-data" },
          onUploadProgress: (event) => {
            if (onProgress && event.total) {
              onProgress(Math.round((event.loaded / event.total) * 100));
            }
          },
        })
        .then((res) => res.data);
    },
    retry: false,
    onSuccess: (result) => {
      if (result?.success) qc.setQueryData(schematicPlanKey(applicationId), { data: result });
    },
  });
}

/**
 * Uploads the plot image of "Upload image + dimensions" and gives back its
 * address. Nothing is saved on the plan: the address goes to the certificate
 * data with the sides and direction.
 */
export function useUploadSchematicImage(applicationId: number) {
  return useMutation({
    mutationFn: (file: File) => {
      const body = new FormData();
      body.append("file", file);
      return _axios
        .post<{ success: boolean; data: { url: string; name: string } }>(
          `${BASE}/${applicationId}/image`,
          body,
          { headers: { "Content-Type": "multipart/form-data" } }
        )
        .then((res) => res.data);
    },
    retry: false,
  });
}

/** Saves Level 1's or Level 2's choice. */
export function useSaveSchematicPlanChoice(applicationId: number) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (planType: TSchematicPlanType) =>
      _axios
        .put<{ success: boolean; data: TSchematicPlan }>(`${BASE}/${applicationId}`, { planType })
        .then((res) => res.data),
    retry: false,
    onSuccess: (result) => {
      if (result?.success) qc.setQueryData(schematicPlanKey(applicationId), { data: result });
    },
  });
}
